import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from '../../../app/LanguageContext';
import {
  VARIABLE_SECTIONS,
  buildVariableCatalog,
  filterVariableEntries,
  groupEntriesBySection,
} from '../private/catalogTemplateVariableCatalog';

const DRAWER_WIDTH = 400;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
};

const VariableGuideRow = ({ row, translate }) => {
  const hasExample = Boolean(row.example && row.example.trim());
  const copyPath = row.path || row.title;

  return (
    <Box
      sx={{
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontFamily="monospace" display="block">
            {row.title}
          </Typography>
          {row.usageKey ? (
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {translate(row.usageKey)}
            </Typography>
          ) : null}
          {row.fields?.length > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              {translate('catalog.templates.variables_fields_available', {
                fields: row.fields.join(', '),
              })}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.25 }}>
          <Tooltip title={translate('catalog.templates.variables_copy_path')}>
            <IconButton
              size="small"
              onClick={() => void copyText(copyPath)}
              aria-label={translate('catalog.templates.variables_copy_path')}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {hasExample ? (
            <Tooltip title={translate('catalog.templates.variables_copy_example')}>
              <IconButton
                size="small"
                onClick={() => void copyText(row.example)}
                aria-label={translate('catalog.templates.variables_copy_example')}
              >
                <ArticleOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      {hasExample ? (
        <Box
          component="pre"
          sx={{
            mt: 1,
            mb: 0,
            p: 1.25,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          {row.example}
        </Box>
      ) : null}
    </Box>
  );
};

const VariablesOverview = ({ entries, translate }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" paragraph>
      {translate('catalog.templates.vars_overview_intro')}
    </Typography>
    <VariablesEntryList entries={entries} translate={translate} emptyKey="catalog.templates.variables_search_empty" />
  </Box>
);

const VariablesEntryList = ({ entries, translate, emptyKey }) => {
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {translate(emptyKey)}
      </Typography>
    );
  }
  return (
    <List dense disablePadding>
      {entries.map((row) => (
        <VariableGuideRow key={row.id} row={row} translate={translate} />
      ))}
    </List>
  );
};

export const TemplateVariablesContent = () => {
  const { translate } = useTranslation();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('overview');

  const catalog = useMemo(() => buildVariableCatalog(), []);

  const filteredEntries = useMemo(
    () => filterVariableEntries(catalog.entries, search),
    [catalog.entries, search],
  );

  const grouped = useMemo(() => groupEntriesBySection(filteredEntries), [filteredEntries]);

  const activeSectionId = search.trim() ? 'all' : tab;

  const visibleEntries = useMemo(() => {
    if (activeSectionId === 'overview') return [];
    if (activeSectionId === 'all') return filteredEntries;
    return grouped.get(activeSectionId) || [];
  }, [activeSectionId, filteredEntries, grouped]);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={search}
        onChange={(e) => {
          const value = e.target.value;
          setSearch(value);
          if (value.trim()) {
            setTab('all');
          } else if (tab === 'all') {
            setTab('overview');
          }
        }}
        placeholder={translate('catalog.templates.variables_search_placeholder')}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {search.trim() ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {translate('catalog.templates.variables_search_results', { count: filteredEntries.length })}
        </Typography>
      ) : (
        <Tabs
          value={tab}
          onChange={(_, value) => value && setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1.5, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
        >
          {VARIABLE_SECTIONS.map((section) => (
            <Tab key={section.id} value={section.id} label={translate(section.titleKey)} />
          ))}
        </Tabs>
      )}

      {activeSectionId === 'overview' && !search.trim() ? (
        <VariablesOverview entries={grouped.get('overview') || []} translate={translate} />
      ) : (
        <VariablesEntryList
          entries={visibleEntries}
          translate={translate}
          emptyKey="catalog.templates.variables_search_empty"
        />
      )}
    </Box>
  );
};

const TemplateVariablesDrawer = ({ open, onClose }) => {
  const { translate } = useTranslation();

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        borderLeft: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {translate('catalog.templates.variables_title')}
        </Typography>
        <IconButton aria-label={translate('common.close')} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, py: 1.5 }}>
        <TemplateVariablesContent />
      </Box>
    </Box>
  );
};

export default TemplateVariablesDrawer;
